import type { ReactElement } from 'react';

import { StringUtils } from '../../utils';
import { EnumFilterMenu, type EnumFilterMenuOption } from './EnumFilterMenu';
import { FilterChip } from './FilterChip';
import {
  type FilterDefinition,
  type FilterEditorComponentProps,
  type FilterMeta,
  FilterViewerComponentProps,
  createFilter,
} from './provider';

export type EnumFilterOption<T extends string> = T | EnumFilterMenuOption<T>;

type EnumFilterOptions<T extends string> = FilterMeta & {
  pluralLabel?: string;
  formatLabel?: (value: T) => string;
  enum: readonly EnumFilterOption<T>[];
} & (
    | { field: string; query?: never }
    | { field?: never; query: (values: T[]) => string }
  );

const normalizeEnumValues = <T extends string>(value: T | T[] | null): T[] => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

const defaultFormatLabel = (value: string): string =>
  StringUtils.capitalize(value.toLowerCase());

export const EnumFilterViewer = <T extends string>(
  props: FilterViewerComponentProps<T[]> & {
    options: readonly T[];
    pluralLabel: string;
    formatLabel?: (value: T) => string;
  },
): ReactElement => {
  const { value, formatLabel = defaultFormatLabel } = props;
  let label: string;

  if (value.length === 0) {
    label = props.filter.label;
  } else if (value.length === 1) {
    if (props.filter.id === 'state' && value[0].toLowerCase() === 'active') {
      const tempLabel = 'Active';
      label = `${props.filter.label}: ${tempLabel}`;
    } else {
      label = `${props.filter.label}: ${formatLabel(value[0])}`;
    }
  } else {
    label = `${props.pluralLabel} (${value.length})`;
  }

  return <FilterChip {...props} label={label} />;
};

export const EnumFilterEditor = <T extends string>(
  props: FilterEditorComponentProps<T[]> & {
    options: readonly EnumFilterOption<T>[];
    formatLabel?: (value: T) => string;
  },
): ReactElement => {
  const { onClose, options, formatLabel = defaultFormatLabel } = props;

  return (
    <EnumFilterMenu
      label={props.filter.label}
      options={options.map(option =>
        typeof option === 'string'
          ? { value: option, label: formatLabel(option) }
          : option,
      )}
      selectedValues={props.value ?? []}
      onValueChange={props.onValueChange}
      onClose={onClose}
      search={
        options.length > 7
          ? {
              placeholder: `Search ${props.filter.label.toLocaleLowerCase()}...`,
            }
          : undefined
      }
    />
  );
};

export const createEnumFilter = <T extends string>(
  config: EnumFilterOptions<T>,
): FilterDefinition => {
  const {
    field,
    enum: enumOptions,
    pluralLabel,
    formatLabel,
    query,
    ...filterMeta
  } = config;

  const options = enumOptions.map(option =>
    typeof option === 'string'
      ? { value: option, label: (formatLabel ?? defaultFormatLabel)(option) }
      : option,
  );

  return createFilter<T[]>({
    ...filterMeta,
    query: value => {
      const values = normalizeEnumValues(value);
      return query
        ? query(values)
        : values.map(v => `${field}:${v}`).join(' OR ');
    },
    viewer: props => (
      <EnumFilterViewer
        options={options.map(option => option.value)}
        pluralLabel={pluralLabel ?? `${filterMeta.label}s`}
        formatLabel={value =>
          options.find(option => option.value === value)?.label ??
          (formatLabel ?? defaultFormatLabel)(value)
        }
        {...props}
        value={normalizeEnumValues(props.value)}
      />
    ),
    editor: props => (
      <EnumFilterEditor
        options={options}
        {...props}
        value={normalizeEnumValues(props.value)}
      />
    ),
  });
};
